import React, { useCallback, useEffect, useState } from "react";
import { Alert, Platform, Pressable, StyleSheet, Switch, Text, View } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

import {
  blockApps,
  clearScheduledShields,
  getCapabilities,
  requestPermission,
  startAppSelection,
  startFocusSchedule,
  stopFocusSchedule,
  unblockApps,
} from "../platform/appBlockingBridge";

const STORAGE_SELECTION = "family_controls_selection_b64";
const STORAGE_SUMMARY = "family_controls_selection_summary";
const STORAGE_BLOCKING = "family_controls_blocking_on";
const STORAGE_SCHEDULE = "family_controls_schedule_on";

function parseSummary(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Screen Time / Family Controls: pick apps (picker), then toggle immediate blocking.
 * Optional focus-hours schedule (requires Device Activity Monitor extension target).
 */
export function FamilyControlsBlockingSection() {
  const [caps, setCaps] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [pickBusy, setPickBusy] = useState(false);
  const [selectionB64, setSelectionB64] = useState(null);
  const [summary, setSummary] = useState(null);
  const [blockingOn, setBlockingOn] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const refreshCaps = useCallback(async () => {
    const c = await getCapabilities();
    setCaps(c);
  }, []);

  useEffect(() => {
    (async () => {
      await refreshCaps();
      const [b64, sum, bOn, sOn] = await Promise.all([
        AsyncStorage.getItem(STORAGE_SELECTION),
        AsyncStorage.getItem(STORAGE_SUMMARY),
        AsyncStorage.getItem(STORAGE_BLOCKING),
        AsyncStorage.getItem(STORAGE_SCHEDULE),
      ]);
      setSelectionB64(b64);
      setSummary(parseSummary(sum));
      setBlockingOn(bOn === "1");
      setScheduleOn(sOn === "1");
    })();
  }, [refreshCaps]);

  const onRequestPermission = async () => {
    setAuthBusy(true);
    try {
      const r = await requestPermission();
      if (r.reason && !r.supported) {
        Alert.alert("Screen Time", r.reason);
      }
      await refreshCaps();
    } catch (e) {
      Alert.alert("Screen Time", e?.message || String(e));
    } finally {
      setAuthBusy(false);
    }
  };

  const onSelectApps = async () => {
    setPickBusy(true);
    try {
      const r = await startAppSelection();
      const b64 = r.selectionBase64;
      const nextSummary = {
        applications: r.applications,
        categories: r.categories,
        webDomains: r.webDomains,
      };
      setSelectionB64(b64);
      setSummary(nextSummary);
      await AsyncStorage.setItem(STORAGE_SELECTION, b64);
      await AsyncStorage.setItem(STORAGE_SUMMARY, JSON.stringify(nextSummary));
      Alert.alert("Selection saved", "Apple does not expose app names to apps; counts reflect your picker choice.");
    } catch (e) {
      const msg = e?.message || String(e);
      if (!/E_USER_CANCELLED|cancelled/i.test(msg)) {
        Alert.alert("Picker", msg);
      }
    } finally {
      setPickBusy(false);
    }
  };

  const onToggleBlocking = async (value) => {
    if (!selectionB64) {
      Alert.alert("Select apps first", 'Tap "Select Apps to Block" before enabling blocking.');
      return;
    }
    try {
      if (value) {
        await blockApps(selectionB64);
        await AsyncStorage.setItem(STORAGE_BLOCKING, "1");
        setBlockingOn(true);
      } else {
        await unblockApps();
        await AsyncStorage.setItem(STORAGE_BLOCKING, "0");
        setBlockingOn(false);
      }
    } catch (e) {
      Alert.alert("Blocking", e?.message || String(e));
    }
  };

  const onToggleSchedule = async (value) => {
    if (!selectionB64) {
      Alert.alert("Select apps first", "Choose apps so the schedule can load tokens from app group storage.");
      return;
    }
    setScheduleBusy(true);
    try {
      if (value) {
        const r = await startFocusSchedule({ startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 });
        if (r?.note) {
          Alert.alert(
            "Focus schedule",
            `${r.note}\n\nDefaults: block 09:00–17:00 local time. Adjust times in platform/appBlockingBridge or native.`,
          );
        }
        await AsyncStorage.setItem(STORAGE_SCHEDULE, "1");
        setScheduleOn(true);
      } else {
        await stopFocusSchedule();
        await clearScheduledShields();
        await AsyncStorage.setItem(STORAGE_SCHEDULE, "0");
        setScheduleOn(false);
      }
    } catch (e) {
      Alert.alert("Schedule", e?.message || String(e));
    } finally {
      setScheduleBusy(false);
    }
  };

  if (Platform.OS !== "ios") {
    return null;
  }

  const pickerOk = !!(caps?.pickerSupported && caps?.authorizationStatus !== "frameworkMissing");

  return (
    <View style={styles.fcBox}>
      <Text style={styles.fcTitle}>App blocking (Screen Time)</Text>
      <Text style={styles.fcHint}>
        Uses Apple FamilyControls + ManagedSettings. Requires physical device; simulator support is limited. App names are never
        exposed to JavaScript — only opaque tokens wrapped in Screen Time picker.
      </Text>

      <Pressable
        style={[styles.fcButton, authBusy ? styles.fcButtonDisabled : null]}
        onPress={onRequestPermission}
        disabled={authBusy}
      >
        <Text style={styles.fcMutedButtonText}>{authBusy ? "Requesting…" : "Request Screen Time permission"}</Text>
      </Pressable>

      <Pressable
        style={[
          styles.fcButtonPrimary,
          pickBusy || !caps?.authorized || !pickerOk ? styles.fcButtonDisabled : null,
        ]}
        onPress={onSelectApps}
        disabled={pickBusy || !caps?.authorized || !pickerOk}
      >
        <Text style={styles.fcButtonText}>{pickBusy ? "Opening picker…" : "Select Apps to Block"}</Text>
      </Pressable>

      <Text style={styles.fcLabel}>Selected (Screen Time)</Text>
      {summary ? (
        <View style={styles.fcList}>
          <Text style={styles.fcListLine}>• Applications: {summary.applications}</Text>
          <Text style={styles.fcListLine}>• Categories: {summary.categories}</Text>
          <Text style={styles.fcListLine}>• Web domains: {summary.webDomains}</Text>
        </View>
      ) : (
        <Text style={styles.fcEmpty}>Nothing selected yet.</Text>
      )}

      <View style={styles.fcRow}>
        <Text style={styles.fcRowLabel}>Enable blocking</Text>
        <Switch value={blockingOn} onValueChange={onToggleBlocking} disabled={!selectionB64} />
      </View>

      <View style={styles.fcRow}>
        <Text style={styles.fcRowLabel}>Focus hours schedule (09:00–17:00)</Text>
        <Switch value={scheduleOn} onValueChange={onToggleSchedule} disabled={!selectionB64 || scheduleBusy} />
      </View>
      <Text style={styles.fcMicro}>
        Schedule needs a Device Activity Monitor extension in Xcode; see native-ios/IOS_BUILD_NOTES.txt.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  fcBox: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#d1d5db",
    paddingTop: 12,
  },
  fcTitle: { fontWeight: "700", fontSize: 17, color: "#1f2937", marginBottom: 8 },
  fcHint: { color: "#4b5563", fontSize: 12, marginBottom: 10, lineHeight: 18 },
  fcButton: {
    borderRadius: 8,
    backgroundColor: "#e5e7eb",
    paddingVertical: 10,
    alignItems: "center",
    marginBottom: 8,
  },
  fcButtonPrimary: {
    borderRadius: 8,
    backgroundColor: "#7c3aed",
    paddingVertical: 10,
    alignItems: "center",
    marginBottom: 10,
  },
  fcButtonDisabled: { opacity: 0.5 },
  fcButtonText: { color: "#fff", fontWeight: "700" },
  fcMutedButtonText: { color: "#111827", fontWeight: "700" },
  fcLabel: { color: "#374151", fontWeight: "600", marginBottom: 6 },
  fcList: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    backgroundColor: "#fff",
    padding: 10,
    marginBottom: 10,
  },
  fcListLine: { color: "#111827", fontSize: 14, marginBottom: 4 },
  fcEmpty: { color: "#6b7280", fontStyle: "italic", marginBottom: 10 },
  fcRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  fcRowLabel: { color: "#374151", fontWeight: "600", flex: 1, paddingRight: 12 },
  fcMicro: { color: "#6b7280", fontSize: 11, marginTop: 4 },
});
